import React, { useState, useRef, useCallback, useEffect } from 'react';
import { Download, Upload, ImageIcon, Turtle } from 'lucide-react';

const sizes = [8, 16, 32, 64];
const scaleOptions = [1, 2, 4, 8, 16, 32];

const PixelArtConverter = () => {
  const [originalImage, setOriginalImage] = useState(null);
  const [pixelizedImages, setPixelizedImages] = useState({});
  const [previewImages, setPreviewImages] = useState({});
  const [selectedSize, setSelectedSize] = useState(16);
  const [scaleMultiplier, setScaleMultiplier] = useState(8);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef(null);

  const pixelizeImage = useCallback((img, targetSize) => {
    // Step 1: Create a small canvas at target resolution
    const smallCanvas = document.createElement('canvas');
    const smallCtx = smallCanvas.getContext('2d');
    
    smallCanvas.width = targetSize;
    smallCanvas.height = targetSize;
    
    // Disable smoothing for pixelated effect
    smallCtx.imageSmoothingEnabled = false;
    smallCtx.webkitImageSmoothingEnabled = false;
    smallCtx.mozImageSmoothingEnabled = false;
    smallCtx.msImageSmoothingEnabled = false;
    
    // Draw image scaled down to target size
    smallCtx.drawImage(img, 0, 0, targetSize, targetSize);
    
    // Step 2: Get the pixel data and create clean pixels
    const imageData = smallCtx.getImageData(0, 0, targetSize, targetSize);
    const data = imageData.data;
    
    // Step 3: Create final canvas at target size (no upscaling here)
    const finalCanvas = document.createElement('canvas');
    const finalCtx = finalCanvas.getContext('2d');
    
    finalCanvas.width = targetSize;
    finalCanvas.height = targetSize;
    
    // Disable smoothing
    finalCtx.imageSmoothingEnabled = false;
    finalCtx.webkitImageSmoothingEnabled = false;
    finalCtx.mozImageSmoothingEnabled = false;
    finalCtx.msImageSmoothingEnabled = false;
    
    // Draw each pixel as a clean rectangle
    for (let y = 0; y < targetSize; y++) {
      for (let x = 0; x < targetSize; x++) {
        const index = (y * targetSize + x) * 4;
        const r = data[index];
        const g = data[index + 1];
        const b = data[index + 2];
        const a = data[index + 3];
        
        if (a > 0) { // Only draw non-transparent pixels
          finalCtx.fillStyle = `rgba(${r}, ${g}, ${b}, ${a / 255})`;
          finalCtx.fillRect(x, y, 1, 1);
        }
      }
    }
    
    return finalCanvas.toDataURL('image/png');
  }, []);

  const createScaledPreview = useCallback((pixelDataUrl, originalSize, scale) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        
        const scaledSize = originalSize * scale;
        canvas.width = scaledSize;
        canvas.height = scaledSize;
        
        // Disable smoothing for pixel-perfect scaling
        ctx.imageSmoothingEnabled = false;
        ctx.webkitImageSmoothingEnabled = false;
        ctx.mozImageSmoothingEnabled = false;
        ctx.msImageSmoothingEnabled = false;
        
        // Draw the image scaled up using nearest neighbor
        ctx.drawImage(img, 0, 0, scaledSize, scaledSize);
        
        resolve(canvas.toDataURL('image/png'));
      };
      img.src = pixelDataUrl;
    });
  }, []);

  const processImage = useCallback((file) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const img = new Image();
      img.onload = () => {
        setOriginalImage({
          src: e.target.result,
          width: img.width,
          height: img.height,
          name: file.name
        });

        // Generate pixelized versions for all sizes
        const newPixelizedImages = {};
        sizes.forEach(size => {
          newPixelizedImages[size] = pixelizeImage(img, size);
        });
        setPixelizedImages(newPixelizedImages);
        
        // Generate initial preview images with current scale
        const generatePreviews = async () => {
          const newPreviewImages = {};
          for (const size of sizes) {
            // Main preview with current scale
            newPreviewImages[size] = await createScaledPreview(newPixelizedImages[size], size, scaleMultiplier);
            // Grid preview with fixed scale for consistency
            const gridScale = Math.max(1, Math.floor(64 / size));
            newPreviewImages[`${size}_grid`] = await createScaledPreview(newPixelizedImages[size], size, gridScale);
          }
          setPreviewImages(newPreviewImages);
        };
        generatePreviews();
      };
      img.onerror = () => {
        alert('Error loading image. Please try a different PNG file.');
      };
      img.src = e.target.result;
    };
    reader.onerror = () => {
      alert('Error reading file. Please try again.');
    };
    reader.readAsDataURL(file);
  }, [pixelizeImage, createScaledPreview, scaleMultiplier]);

  // Regenerate preview images when scale multiplier changes
  useEffect(() => {
    if (Object.keys(pixelizedImages).length === 0) return;
    
    const generatePreviews = async () => {
      const newPreviewImages = {};
      for (const size of sizes) {
        if (pixelizedImages[size]) {
          // Main preview with current scale
          newPreviewImages[size] = await createScaledPreview(pixelizedImages[size], size, scaleMultiplier);
          // Grid preview with fixed scale for consistency
          const gridScale = Math.max(1, Math.floor(64 / size));
          newPreviewImages[`${size}_grid`] = await createScaledPreview(pixelizedImages[size], size, gridScale);
        }
      }
      setPreviewImages(newPreviewImages);
    };
    generatePreviews();
  }, [scaleMultiplier, pixelizedImages, createScaledPreview]);

  const handleFileSelect = (file) => {
    if (file && file.type === 'image/png') {
      processImage(file);
    } else if (file && file.type.startsWith('image/')) {
      // Allow other image types but warn user
      const confirmed = window.confirm('This app works best with PNG files to preserve transparency. Continue anyway?');
      if (confirmed) {
        processImage(file);
      }
    } else {
      alert('Please select a PNG image file.');
    }
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      handleFileSelect(file);
      // Reset the input value so the same file can be selected again
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleFileSelect(file);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };


  const downloadImage = (size, scale = 1) => {
    if (!pixelizedImages[size] || !originalImage) return;
    
    let dataUrl;
    let filename;
    
    if (scale === 1) {
      dataUrl = pixelizedImages[size];
      filename = `${originalImage.name.replace(/\.[^/.]+$/, '')}_${size}x${size}.png`;
    } else {
      // Create scaled version
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      canvas.width = size * scale;
      canvas.height = size * scale;
      
      // Disable smoothing for pixel-perfect scaling
      ctx.imageSmoothingEnabled = false;
      ctx.webkitImageSmoothingEnabled = false;
      ctx.mozImageSmoothingEnabled = false;
      ctx.msImageSmoothingEnabled = false;
      
      const img = new Image();
      img.onload = () => {
        ctx.drawImage(img, 0, 0, size * scale, size * scale);
        
        const link = document.createElement('a');
        link.download = `${originalImage.name.replace(/\.[^/.]+$/, '')}_${size}x${size}_scaled${scale}x.png`;
        link.href = canvas.toDataURL('image/png');
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
      };
      img.src = pixelizedImages[size];
      return;
    }
    
    const link = document.createElement('a');
    link.download = filename;
    link.href = dataUrl;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const downloadAll = () => {
    if (!originalImage || Object.keys(pixelizedImages).length === 0) return;
    
    sizes.forEach((size, index) => {
      setTimeout(() => downloadImage(size, scaleMultiplier), index * 100); // Stagger downloads
    });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-6">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-8">
          <Turtle className="w-12 h-12 mx-auto mb-4 text-gray-700" />
          <h1 className="text-2xl font-medium text-gray-900 mb-2">PNG to Pixel Art Converter</h1>
          <p className="text-sm text-gray-600">Convert your PNG images into crisp pixel art icons (perfect for favicons!)</p>
        </div>

        {/* File Upload Area */}
        <div
          className={`border-2 border-dashed rounded-xl p-12 text-center transition-all duration-200 mb-8 ${
            isDragging 
              ? 'border-gray-400 bg-gray-100' 
              : 'border-gray-300 bg-white hover:border-gray-400'
          }`}
          onDrop={handleDrop}
          onDragOver={handleDragOver}
          onDragLeave={handleDragLeave}
        >
          <input
            type="file"
            accept="image/*"
            onChange={handleFileChange}
            className="hidden"
            ref={fileInputRef}
          />
          
          <div className="flex flex-col items-center">
            <Upload className="w-8 h-8 text-gray-400 mb-4" />
            <p className="text-base font-medium text-gray-900 mb-1">
              Drop your PNG file here or click to browse
            </p>
            <p className="text-sm text-gray-500 mb-6">
              Supports PNG files with transparency
            </p>
            <button
              onClick={() => fileInputRef.current?.click()}
              className="px-6 py-2 bg-gray-900 text-white text-sm rounded-xl hover:bg-gray-800 transition-colors duration-200"
            >
              Select Image
            </button>
          </div>
        </div>

        {originalImage && (
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Original Image */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-medium mb-4 flex items-center text-gray-900">
                <ImageIcon className="w-5 h-5 mr-2" />
                Original Image
              </h2>
              <div className="flex flex-col items-center">
                <div className="relative bg-gray-50 rounded-xl p-4 mb-4 max-w-full overflow-hidden">
                  <img
                    src={originalImage.src}
                    alt="Original"
                    className="max-w-full max-h-64 object-contain"
                    style={{ imageRendering: 'auto' }}
                  />
                </div>
                <p className="text-sm text-gray-500">
                  {originalImage.width} × {originalImage.height} pixels
                </p>
              </div>
            </div>

            {/* Pixel Art Versions */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <h2 className="text-lg font-medium mb-4 text-gray-900">Pixel Art Versions</h2>
              
              {/* Size Selector */}
              <div className="flex flex-wrap gap-2 mb-4">
                {sizes.map(size => (
                  <button
                    key={size}
                    onClick={() => setSelectedSize(size)}
                    className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                      selectedSize === size
                        ? 'bg-blue-500 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {size}×{size}
                  </button>
                ))}
              </div>

              {/* Scale Selector */}
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Scale: {scaleMultiplier}x
                </label>
                <div className="flex flex-wrap gap-2">
                  {scaleOptions.map(scale => (
                    <button
                      key={scale}
                      onClick={() => setScaleMultiplier(scale)}
                      className={`px-3 py-1 rounded-full text-sm font-medium transition-colors duration-200 ${
                        scaleMultiplier === scale
                          ? 'bg-green-500 text-white'
                          : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                      }`}
                    >
                      {scale}x
                    </button>
                  ))}
                </div>
              </div>

              {/* Selected Size Preview */}
              <div className="flex flex-col items-center mb-6">
                <div 
                  className="relative bg-gray-100 rounded-lg p-4 mb-4 flex items-center justify-center"
                  style={{
                    width: '320px',
                    height: '320px'
                  }}
                >
                  <div className="bg-white bg-opacity-50 absolute inset-0 rounded-lg" 
                       style={{
                         backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                         backgroundSize: '10px 10px',
                         backgroundPosition: '0 0, 0 5px, 5px -5px, -5px 0px'
                       }}>
                  </div>
                  <img
                    src={previewImages[selectedSize] || pixelizedImages[selectedSize]}
                    alt={`${selectedSize}x${selectedSize} pixel art`}
                    className="relative z-10 pixel-art"
                    style={{ 
                      maxWidth: '280px',
                      maxHeight: '280px',
                      objectFit: 'none',
                      imageRendering: 'pixelated'
                    }}
                  />
                </div>
                <p className="text-sm text-gray-600 mb-4">
                  {selectedSize} × {selectedSize} pixels (scaled {scaleMultiplier}x for preview)
                </p>
                
                <button
                  onClick={() => downloadImage(selectedSize, scaleMultiplier)}
                  className="flex items-center px-4 py-2 bg-green-500 text-white rounded-lg hover:bg-green-600 transition-colors duration-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download {selectedSize}×{selectedSize} {scaleMultiplier > 1 ? `(scaled ${scaleMultiplier}x)` : ''}
                </button>
              </div>

              {/* All Sizes Grid */}
              <div className="border-t pt-6">
                <h3 className="text-lg font-medium mb-4">All Sizes</h3>
                <div className="grid grid-cols-4 gap-4 mb-4">
                  {sizes.map(size => (
                    <div key={size} className="text-center">
                      <div className="bg-gray-100 rounded-lg p-2 mb-2 relative w-20 h-20 flex items-center justify-center overflow-hidden mx-auto">
                        <div className="bg-white bg-opacity-50 absolute inset-0 rounded-lg" 
                             style={{
                               backgroundImage: 'linear-gradient(45deg, #ccc 25%, transparent 25%), linear-gradient(-45deg, #ccc 25%, transparent 25%), linear-gradient(45deg, transparent 75%, #ccc 75%), linear-gradient(-45deg, transparent 75%, #ccc 75%)',
                               backgroundSize: '8px 8px',
                               backgroundPosition: '0 0, 0 4px, 4px -4px, -4px 0px'
                             }}>
                        </div>
                        <img
                          src={previewImages[`${size}_grid`] || pixelizedImages[size]}
                          alt={`${size}x${size}`}
                          className="relative z-10 pixel-art"
                          style={{ 
                            maxWidth: '64px',
                            maxHeight: '64px',
                            objectFit: 'none',
                            imageRendering: 'pixelated'
                          }}
                        />
                      </div>
                      <p className="text-xs text-gray-600">{size}×{size}</p>
                    </div>
                  ))}
                </div>
                
                <button
                  onClick={downloadAll}
                  className="w-full flex items-center justify-center px-4 py-2 bg-purple-500 text-white rounded-lg hover:bg-purple-600 transition-colors duration-200"
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download All Sizes {scaleMultiplier > 1 ? `(scaled ${scaleMultiplier}x)` : ''}
                </button>
              </div>
            </div>
          </div>
        )}

      </div>
    </div>
  );
};

export default PixelArtConverter;